import { useEffect, useRef, useState, useCallback } from "react";

function AnimatedCounter({
  target,
  suffix = "",
  prefix = "",
  duration = 2000,
  decimals = 0,
}: {
  target: number;
  suffix?: string;
  prefix?: string;
  duration?: number;
  decimals?: number;
}) {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const inViewRef = useRef(false);
  const startTime = useRef<number | null>(null);
  const rafId = useRef<number>(0);

  const animate = useCallback(
    (timestamp: number) => {
      if (startTime.current === null) startTime.current = timestamp;
      const elapsed = timestamp - startTime.current;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 4);
      setValue(ease * target);
      if (progress < 1) {
        rafId.current = requestAnimationFrame(animate);
      }
    },
    [target, duration]
  );

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !inViewRef.current) {
          inViewRef.current = true;
          startTime.current = null;
          rafId.current = requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [animate]);

  const formatted =
    decimals > 0 ? value.toFixed(decimals) : Math.round(value).toString();

  return (
    <span ref={ref} className="inline-block">
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}

const stats = [
  { value: 90, suffix: "%", label: "Réparés", sublabel: "On répare avant de remplacer" },
  { value: 10, suffix: " ans", label: "Garantie", sublabel: "Pièces et main-d'œuvre" },
  { value: 30, suffix: " min", label: "Intervention", sublabel: "Diagnostic rapide sur place" },
  { value: 0, suffix: "€", label: "Déplacement", sublabel: "Devis gratuit, sans engagement" },
];

const reasons = [
  {
    icon: "ri-map-pin-2-line",
    title: "Entreprise locale",
    desc: "Atelier basé à Strasbourg, techniciens du Bas-Rhin. On connaît vos fenêtres et vos quartiers.",
  },
  {
    icon: "ri-recycle-line",
    title: "Réparer plutôt que remplacer",
    desc: "Dans plus de 90% des cas, on répare le mécanisme existant. Économique et écologique.",
  },
  {
    icon: "ri-price-tag-3-line",
    title: "Pièce comprise, toutes marques",
    desc: "Ferco, GU, Roto, Maco, Siegenia... Nous avons (ou trouvons) la pièce adaptée à votre fenêtre.",
  },
  {
    icon: "ri-file-list-3-line",
    title: "Devis et déplacement gratuits",
    desc: "Pas de surprise. Le diagnostic et le déplacement sont offerts, même si vous ne faites pas réparer.",
  },
];

export default function WhyUs() {
  return (
    <section className="bg-rose-50 py-10 md:py-14 lg:py-16">
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-[1280px] mx-auto">
        <div className="text-center mb-8 md:mb-10">
          <p className="font-serif italic text-plum-dark text-sm md:text-base mb-3">
            Pourquoi faire appel à DF PRO
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-plum mb-4 leading-tight">
            <span className="font-serif italic">La</span> réparation{" "}
            <span className="font-serif italic">avant</span> le remplacement
          </h2>
          <p className="text-plum-dark text-base md:text-lg max-w-2xl mx-auto">
            Changer une fenêtre coûte des milliers d'euros. Réparer le mécanisme coûte 10 fois moins cher.
          </p>
        </div>

        {/* Compteurs animés */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8 md:mb-10">
          {stats.map((stat, i) => (
            <div
              key={i}
              className="relative bg-white rounded-2xl border border-rose-200 p-6 md:p-8 text-center overflow-hidden hover:border-coral transition-colors duration-300 group"
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-plum via-coral to-coral origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
              <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-plum mb-2 tabular-nums">
                <AnimatedCounter
                  target={stat.value}
                  suffix={stat.suffix}
                  duration={2200}
                />
              </div>
              <div className="text-sm font-bold text-plum-dark mb-1 uppercase tracking-wider">
                {stat.label}
              </div>
              {stat.sublabel && (
                <div className="text-xs text-coral font-medium">{stat.sublabel}</div>
              )}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {reasons.map((r) => (
            <div
              key={r.title}
              className="bg-white rounded-2xl border border-rose-200 p-6 md:p-8 hover:border-coral transition-colors duration-300"
            >
              <div className="w-12 h-12 rounded-xl bg-rose-50 flex items-center justify-center mb-5">
                <i className={`${r.icon} text-plum text-xl`} />
              </div>
              <h3 className="text-base font-bold text-plum mb-3">{r.title}</h3>
              <p className="text-sm text-plum-dark leading-relaxed">{r.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}